import React, { useEffect, useState, useCallback } from "react";
import api, { fileUrl } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Thumb, DateRange, TabBar, Row, fmtDate } from "@/components/list";
import { Button } from "@/components/ui/button";
import { Rocket, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TABS = ["Ready to Launch", "Launched"];
const stageFor = (t) => (t === "Launched" ? "Launched" : "Approved");

const ReadyToLaunch = () => {
  const [tab, setTab] = useState("Ready to Launch");
  const [items, setItems] = useState({ "Ready to Launch": [], "Launched": [] });
  const [from, setFrom] = useState(""); const [to, setTo] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    const base = {};
    if (from) base.date_from = from;
    if (to) base.date_to = to;
    try {
      const [a, l] = await Promise.all([
        api.get("/briefs", { params: { ...base, stage: "Approved" } }),
        api.get("/briefs", { params: { ...base, stage: "Launched" } }),
      ]);
      setItems({ "Ready to Launch": a.data, "Launched": l.data });
    } catch (e) { toast.error("Failed to load"); }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const launch = async (id) => {
    setBusy(id);
    try { await api.put(`/briefs/${id}/stage`, { stage: "Launched" }); toast.success("Launched 🚀"); load(); }
    catch (e) { toast.error("Could not launch"); }
    setBusy(null);
  };

  const counts = { "Ready to Launch": items["Ready to Launch"].length, "Launched": items["Launched"].length };
  const list = items[tab];

  return (
    <div className="space-y-6">
      <div><h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Ready to Launch</h1>
        <p className="text-zinc-400 mt-1">Approved briefs waiting to go live, and everything already launched.</p></div>

      <div className="flex flex-wrap items-center gap-3 justify-between">
        <TabBar tabs={TABS} value={tab} onChange={setTab} counts={counts} testidPrefix="launch-tab" />
        <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
      </div>

      <div className="space-y-3">
        {list.map((b) => (
          <Row key={b.id} testid={`launch-brief-${b.id}`}>
            <Thumb src={(b.thumbnail_path || b.client_thumbnail) ? fileUrl(b.thumbnail_path || b.client_thumbnail) : null} href={b.google_doc_link} alt={b.title} />
            <div className="min-w-0 flex-1">
              <p className="font-semibold truncate">{b.title}</p>
              <p className="text-zinc-400 text-sm truncate">{b.client_name} • {b.num_creatives || 0} creatives • Created {fmtDate(b.created_at)}</p>
            </div>
            <StatusBadge status={b.stage} />
            {tab === "Ready to Launch" && (
              <Button data-testid={`launch-button-${b.id}`} onClick={() => launch(b.id)} disabled={busy === b.id} size="sm" className="rounded-full bg-brand text-ink-900 hover:bg-brand-hover font-semibold shrink-0">
                {busy === b.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Rocket className="w-4 h-4 mr-1" /> Launch</>}
              </Button>
            )}
          </Row>
        ))}
        {list.length === 0 && <p className="text-zinc-500 py-8 text-center">Nothing here yet.</p>}
      </div>
    </div>
  );
};

export default ReadyToLaunch;
